import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import Radom, { ManagedBalanceLedger, RefundEntity, WithdrawalAccount, WithdrawalRequest } from '../state/Radom'
import TimeText from '../components/TimeText'
import { Container } from '../components/Animations'
import PaymentMethodDisplay from '../components/PaymentMethodDisplay'
import { getMethod, ManagedPaymentMethod } from '../util/Managed'
import BreadCrumbs from '../components/Breadcrumbs'
import { useParams, useSearchParams } from 'react-router-dom'
import { RADOM_COLORS } from '../util/Constants'
import Spinner from '../components/Spinner'
import { IconButton } from '../components/Button'
import Copy from '../icons/Copy'
import { errorToast, roundAssetAmount, successToast, trimZeros } from '../util/Util'
import { SectionContent, SectionItem, SubTitle, Section } from '../components/Shared'
import _ from 'lodash'
import DestinationDetails from '../components/DestinationDetails'
import WithdrawalDetails from '../components/WithdrawalDetails'
import { ledgerBalanceLabel } from '../util/Ledger'
import {
  amountRowStyle,
  assetLogo32Style,
  assetRowStyle,
  containerStyle,
  copyIconStyle,
  iconButtonNoPaddingStyle,
  idRowStyle,
  ledgerRowStyle,
  ledgerSecondaryTextStyle,
  loadingWrapperStyle,
  paymentMethodMarginRight4Style,
  refundHeaderAmountRowStyle,
  refundHeaderStyle,
  sectionTitle18Style,
  tickerBadgeStyle,
  topBarStyle
} from './RefundStyles'

/** The balance a ledger row is for: "USDC on Polygon", or "USDC · All networks" for a pooled balance. */
export const Refund = (): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')

  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger>()
  const [isLoading, setIsLoading] = useState(false)
  const [refund, setRefund] = useState<RefundEntity>()
  const [withdrawal, setWithdrawal] = useState<WithdrawalRequest>()
  const [receiptAccount, setReceiptAccount] = useState<WithdrawalAccount>()
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()

  const { accountName, accountId } = useMemo(() => {
    const accountName = receiptAccount?.name
    const accountId = receiptAccount?.id
    return { accountName, accountId }
  }, [receiptAccount])

  const { transactionHash, networkFeeAmount } = useMemo(() => {
    const evm = withdrawal?.receipt?.EVMReceipt
    const dot = withdrawal?.receipt?.DOTReceipt
    const btc = withdrawal?.receipt?.BTCReceipt
    const sol = withdrawal?.receipt?.SOLReceipt

    const transactionHash =
      evm?.transactionHash ||
      dot?.transactionHash ||
      btc?.transactionId ||
      sol?.signature

    const networkFeeAmount =
      evm?.networkFeeAmount ||
      dot?.networkFeeAmount ||
      btc?.networkFeeAmount ||
      sol?.networkFeeAmount

    return { transactionHash, networkFeeAmount }
  }, [withdrawal])

  const finalAmountCrypto = useMemo(() => {
    if (!withdrawal || !networkFeeAmount) return undefined
    return trimZeros((Number(withdrawal.withdrawalAmount) - Number(networkFeeAmount)).toString())
  }, [withdrawal, networkFeeAmount])

  const createdAtDate = useMemo(() => {
    if (!refund?.createdAt) return null
    return new Date(refund.createdAt)
  }, [refund?.createdAt])

  const breadcrumbsItems = useMemo(() => {
    if (!refund) return []
    return source === 'transactions'
      ? [
        { item: 'Transactions', to: '/transactions' },
        { item: refund.id, to: `/refund/${refund.id}?source=transactions` }
      ]
      : [
        { item: 'Transactions', to: '/transactions' },
        { item: refund.id, to: '.' }
      ]
  }, [refund, source])

  const withdrawalPaymentMethod = useMemo(() => {
    if (!withdrawal) return undefined
    return getMethod(withdrawal.network, withdrawal.token)
  }, [withdrawal])

  const fetchRefundData = useCallback(async (id: string): Promise<void> => {
    setIsLoading(true)

    let loadedRefund: RefundEntity
    try {
      loadedRefund = await Radom.getRefund(id)
      setRefund(loadedRefund)
      setPaymentMethod(getMethod(loadedRefund.network, loadedRefund.token))
    } catch {
      setIsLoading(false)
      errorToast('Failed to load refund')
      return
    }

    try {
      const ledger = await Radom.listManagedBalanceLedger([loadedRefund.id])
      if (ledger.length > 0) setManagedBalanceLedger(ledger[0])
    } catch {
      errorToast('Failed to load ledger')
    }

    if (loadedRefund.withdrawalId) {
      let loadedWithdrawal: WithdrawalRequest
      try {
        loadedWithdrawal = await Radom.getWithdrawal(loadedRefund.withdrawalId)
        setWithdrawal(loadedWithdrawal)
      } catch {
        setIsLoading(false)
        errorToast('Failed to load withdrawal')
        return
      }

      const accountId = loadedWithdrawal.withdrawalAccountId

      try {
        if (accountId) {
          const acc = await Radom.getWithdrawalAccount(accountId)
          setReceiptAccount(acc)
        } else {
          const accs = await Radom.listWithdrawalAccounts()
          const actualAccount =
            accs.find(acc => acc.accountDetails.Crypto?.address === loadedWithdrawal?.withdrawalAddress)
          setReceiptAccount(actualAccount)
        }
      } catch {
        errorToast('Failed to load withdrawal account')
      }
    }

    setIsLoading(false)
  }, [])

  const debouncedFetchRefundData = useMemo(
    () => _.debounce(fetchRefundData, 300),
    [fetchRefundData]
  )

  useEffect(() => {
    if (!params.id) return
    debouncedFetchRefundData(params.id)
    return () => debouncedFetchRefundData.cancel()
  }, [params.id, debouncedFetchRefundData])

  if (isLoading) {
    return (
      <div style={loadingWrapperStyle}>
        <Spinner />
      </div>
    )
  }

  if (!refund || !paymentMethod) {
    return <></>
  }

  return (
    <Container style={containerStyle}>
      <div style={topBarStyle}>
        <BreadCrumbs items={breadcrumbsItems} />
      </div>

      <Section>
        <div style={refundHeaderStyle}>
          <div style={refundHeaderAmountRowStyle}>
            <img src={paymentMethod.logo} style={assetLogo32Style} />
            <span>{refund.amount}</span>
            <span style={tickerBadgeStyle}>{paymentMethod.ticker}</span>
          </div>
        </div>

        <SectionContent>
          <SectionItem>
            <SubTitle>ID</SubTitle>
            <div style={idRowStyle}>
              <span>{refund.id}</span>

              <IconButton style={iconButtonNoPaddingStyle}>
                <Copy
                  style={copyIconStyle}
                  stroke={RADOM_COLORS.GRAY_DARKER}
                  onClick={(e) => {
                    e.stopPropagation()
                    navigator.clipboard
                      .writeText(refund.id)
                      .then(() => {
                        successToast('Copied refund id')
                      })
                      .catch((err) => {
                        console.error('copy failed', err)
                        errorToast('Failed to copy refund id')
                      })
                  }}
                />
              </IconButton>
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Refund date</SubTitle>
            {createdAtDate && <TimeText textColor={RADOM_COLORS.BLACK} date={createdAtDate} />}
          </SectionItem>
        </SectionContent>
      </Section>

      {managedBalanceLedger && (
        <Section>
          <SubTitle style={sectionTitle18Style}>Ledger</SubTitle>
          <SectionContent>
            <SectionItem>
              <SubTitle>Balance Before</SubTitle>
              <div style={ledgerRowStyle}>
                <span>{roundAssetAmount(paymentMethod.ticker, managedBalanceLedger.balanceBefore)}</span>
                <span style={ledgerSecondaryTextStyle}>
                  {ledgerBalanceLabel(managedBalanceLedger, paymentMethod.ticker)}
                </span>
              </div>
            </SectionItem>

            <SectionItem>
              <SubTitle>Balance After</SubTitle>
              <div style={ledgerRowStyle}>
                <span>{roundAssetAmount(paymentMethod.ticker, managedBalanceLedger.balanceAfter)}</span>
                <span style={ledgerSecondaryTextStyle}>
                  {ledgerBalanceLabel(managedBalanceLedger, paymentMethod.ticker)}
                </span>
              </div>
            </SectionItem>
          </SectionContent>
        </Section>
      )}

      <Section>
        <SubTitle style={sectionTitle18Style}>Refund details</SubTitle>

        <SectionContent>
          <SectionItem>
            <SubTitle>Asset</SubTitle>
            <div style={assetRowStyle}>
              <PaymentMethodDisplay
                style={paymentMethodMarginRight4Style}
                paymentMethod={paymentMethod}
                method={{ network: refund.network }}
                includeNetworkName
                disableTooltip={false}
              />
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Amount</SubTitle>
            <div style={amountRowStyle}>
              <PaymentMethodDisplay
                style={paymentMethodMarginRight4Style}
                method={{ network: refund.network, token: refund.token }}
                excludeName
                disableTooltip={false}
              />
              <div style={amountRowStyle}>
                <span>{refund.amount}</span>
                <span>{paymentMethod.ticker}</span>
              </div>
            </div>
          </SectionItem>

          <SectionItem>
            <SubTitle>Created at</SubTitle>
            {createdAtDate && <TimeText textColor={RADOM_COLORS.BLACK} date={createdAtDate} />}
          </SectionItem>
        </SectionContent>
      </Section>

      <Section>
        <SubTitle style={sectionTitle18Style}>Refund destination</SubTitle>

        <SectionContent>
          {refund.recipientAddress && (
            <SectionItem>
              <SubTitle>Recipient address</SubTitle>
              <div style={idRowStyle}>
                <span>{refund.recipientAddress}</span>

                <IconButton style={iconButtonNoPaddingStyle}>
                  <Copy
                    style={copyIconStyle}
                    stroke={RADOM_COLORS.GRAY_DARKER}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigator.clipboard
                        .writeText(refund.recipientAddress ?? '')
                        .then(() => {
                          successToast('Copied refund recipient address')
                        })
                        .catch((err) => {
                          console.error('copy failed', err)
                          errorToast('Failed to copy refund recipient address')
                        })
                    }}
                  />
                </IconButton>
              </div>
            </SectionItem>
          )}

          {refund.recipientAccount?.iban && (
            <>
              {refund.recipientAccount.iban.bic && (
                <SectionItem>
                  <SubTitle>BIC</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.iban.bic}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.iban?.bic ?? '')
                            .then(() => successToast('Copied refund bic'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund bic')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}

              {refund.recipientAccount.iban.iban && (
                <SectionItem>
                  <SubTitle>IBAN</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.iban.iban}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.iban?.iban ?? '')
                            .then(() => successToast('Copied refund iban'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund iban')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}

              {refund.recipientAccount.iban.accountHolderName && (
                <SectionItem>
                  <SubTitle>Account holder name</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.iban.accountHolderName}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.iban?.accountHolderName ?? '')
                            .then(() => successToast('Copied refund account holder name'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund account holder name')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}
            </>
          )}

          {refund.recipientAccount?.bankCode && (
            <>
              {refund.recipientAccount.bankCode.accountNumber && (
                <SectionItem>
                  <SubTitle>Account number</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.bankCode.accountNumber}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.bankCode?.accountNumber ?? '')
                            .then(() => successToast('Copied refund account number'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy account number')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}

              {refund.recipientAccount.bankCode.code && (
                <SectionItem>
                  <SubTitle>CODE</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.bankCode.code}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.bankCode?.code ?? '')
                            .then(() => successToast('Copied refund code'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund code')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}

              {refund.recipientAccount.bankCode.accountHolderName && (
                <SectionItem>
                  <SubTitle>Account holder name</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.bankCode.accountHolderName}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.bankCode?.accountHolderName ?? '')
                            .then(() => successToast('Copied refund account holder name'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund account holder name')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}
            </>
          )}

          {refund.recipientAccount?.bban && (
            <>
              {refund.recipientAccount.bban.bic && (
                <SectionItem>
                  <SubTitle>BIC</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.bban.bic}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.bban?.bic ?? '')
                            .then(() => successToast('Copied refund bic'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund bic')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}

              {refund.recipientAccount.bban.bban && (
                <SectionItem>
                  <SubTitle>BBAN</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.bban.bban}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.bban?.bban ?? '')
                            .then(() => successToast('Copied refund bban'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund bban')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}

              {refund.recipientAccount.bban.accountHolderName && (
                <SectionItem>
                  <SubTitle>Account holder name</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.bban.accountHolderName}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.bban?.accountHolderName ?? '')
                            .then(() => successToast('Copied refund account holder name'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund account holder name')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}
            </>
          )}

          {refund.recipientAccount?.sortCode && (
            <>
              {refund.recipientAccount.sortCode.accountNumber && (
                <SectionItem>
                  <SubTitle>Account number</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.sortCode.accountNumber}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.sortCode?.accountNumber ?? '')
                            .then(() => successToast('Copied refund account number'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund account number')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}

              {refund.recipientAccount.sortCode.sortCode && (
                <SectionItem>
                  <SubTitle>SORT CODE</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.sortCode.sortCode}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.sortCode?.sortCode ?? '')
                            .then(() => successToast('Copied refund sort code'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund sort code')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}

              {refund.recipientAccount.sortCode.accountHolderName && (
                <SectionItem>
                  <SubTitle>Account holder name</SubTitle>
                  <div style={idRowStyle}>
                    <span>{refund.recipientAccount.sortCode.accountHolderName}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        stroke={RADOM_COLORS.GRAY_DARKER}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard
                            .writeText(refund.recipientAccount?.sortCode?.accountHolderName ?? '')
                            .then(() => successToast('Copied refund account holder name'))
                            .catch((err) => {
                              console.error('copy failed', err)
                              errorToast('Failed to copy refund account holder name')
                            })
                        }}
                      />
                    </IconButton>
                  </div>
                </SectionItem>
              )}
            </>
          )}
        </SectionContent>
      </Section>

      {withdrawal && (
        <>
          <DestinationDetails
            isFiatWithdrawal={false}
            addressTo={withdrawal.withdrawalAddress}
            transferMethod={withdrawal.withdrawalStatus === 'complete' ? 'Crypto' : undefined}
            accountName={accountName}
            accountId={accountId}
          />

          {withdrawal.withdrawalStatus === 'complete' && withdrawalPaymentMethod && (
            <WithdrawalDetails
              paymentMethod={withdrawalPaymentMethod}
              requestedAt={withdrawal.requestedAt}
              completedAt={withdrawal.completedAt}
              transactionHash={transactionHash}
              network={withdrawal.network}
              networkFeeAmount={networkFeeAmount}
              finalAmountCrypto={finalAmountCrypto}
              amount={withdrawal.withdrawalAmount}
            />
          )}
        </>
      )}
    </Container>
  )
}
